import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface Enrollment {
  factorId: string;
  qrCode: string;
  secret: string;
}

const AdminSecurity = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/admin";
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [factorId, setFactorId] = useState("");
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prepare = async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setAuthenticated(false);
        setLoading(false);
        return;
      }
      setAuthenticated(true);
      const { data: assurance } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assurance?.currentLevel === "aal2") {
        const { error } = await supabase.functions.invoke("admin-geo-check", { body: {} });
        if (error) {
          toast.error("Der Adminzugriff ist an diesem Standort nicht verfügbar.");
          await supabase.auth.signOut();
          setAuthenticated(false);
        } else {
          navigate(from, { replace: true });
        }
        return;
      }
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) {
        toast.error(factorsError.message);
        setLoading(false);
        return;
      }
      const verifiedFactor = factors.totp[0];
      if (verifiedFactor) {
        setFactorId(verifiedFactor.id);
      } else {
        const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "FACHSPRECHPARTNER Admin" });
        if (error) {
          toast.error(error.message);
        } else {
          setFactorId(data.id);
          setEnrollment({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
        }
      }
      setLoading(false);
    };
    prepare();
  }, [from, navigate]);

  const verify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Bitte geben Sie den sechsstelligen Code ein.");
      return;
    }
    setLoading(true);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code });
      if (verifyError) throw verifyError;
      const { error: geoError } = await supabase.functions.invoke("admin-geo-check", { body: {} });
      if (geoError) {
        toast.error("Der Adminzugriff ist an diesem Standort nicht verfügbar.");
        await supabase.auth.signOut();
        setAuthenticated(false);
        return;
      }
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Der Code konnte nicht bestätigt werden.");
    } finally {
      setLoading(false);
    }
  };

  if (authenticated === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-8">
      <Helmet><title>Sicherheitsprüfung | FACHSPRECHPARTNER</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <ShieldCheck className="mx-auto mb-2 h-10 w-10 text-secondary" />
          <CardTitle>{enrollment ? "Authenticator-App einrichten" : "Anmeldung bestätigen"}</CardTitle>
          <CardDescription>{enrollment ? "Scannen Sie den QR-Code mit Ihrer Authenticator-App und geben Sie den angezeigten Code ein." : "Geben Sie den Code aus Ihrer Authenticator-App ein."}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {enrollment && (
            <div className="space-y-3 text-center">
              <img src={enrollment.qrCode} alt="QR-Code für die Authenticator-App" className="mx-auto h-48 w-48 rounded-md border bg-background p-2" />
              <div className="text-left">
                <Label>Schlüssel für manuelle Eingabe</Label>
                <p className="mt-1 break-all rounded-md bg-muted p-3 font-mono text-sm">{enrollment.secret}</p>
              </div>
            </div>
          )}
          <form onSubmit={verify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mfa-code">Sechsstelliger Code</Label>
              <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} disabled={loading || !factorId} />
            </div>
            <Button type="submit" className="w-full" disabled={loading || !factorId || code.length !== 6}>{loading ? "Wird geprüft…" : "Sicher anmelden"}</Button>
          </form>
          <Button variant="ghost" className="w-full" onClick={() => supabase.auth.signOut()}>Abmelden</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSecurity;